import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { merge, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { LEARNING_HUB_I18N_URL } from '../definitions/learning-hub.constants';
import { LearningHubLanguage } from '../definitions/learning-hub.models';

type Translations = Record<string, unknown>;

/**
 * UI texts of the Learning Hub in the language of the content (?lang=en shows the whole hub in English, like the
 * /en/ site of the WordPress), without changing the language of the rest of the app.
 * Uses the translations already loaded by ngx-translate when available; otherwise the i18n file of that
 * language is fetched once (same files as the app: src/assets/i18n/<lang>.json). While it loads, or for a missing
 * key, the app language is used.
 */
@Injectable()
export class LearningHubI18nService implements OnDestroy {
  private readonly CACHE = new Map<LearningHubLanguage, Translations>();
  private readonly LOADING = new Set<LearningHubLanguage>();
  private readonly CHANGES$ = new Subject<void>();

  constructor(private readonly _http: HttpClient, private readonly _translate: TranslateService) {}

  /** Emits when new texts are available (a language loaded here, or the app translations changed). */
  get changes$(): Observable<void> {
    return merge(
      this.CHANGES$,
      this._translate.onLangChange,
      this._translate.onTranslationChange,
      this._translate.onDefaultLangChange
    ).pipe(map((): void => undefined));
  }

  instant(key: string, lang: LearningHubLanguage | null | undefined, params?: Record<string, unknown>): string {
    const translations = lang ? this._translations(lang) : null;
    if (translations) {
      const value = this._translate.getParsedResult(translations, key, params);
      if (typeof value === 'string' && value !== key) {
        return value;
      }
    }
    return this._translate.instant(key, params);
  }

  ngOnDestroy(): void {
    this.CHANGES$.complete();
  }

  private _translations(lang: LearningHubLanguage): Translations | null {
    const loaded = (this._translate.translations || {})[lang] || this.CACHE.get(lang);
    if (loaded) {
      return loaded;
    }
    if (!this.LOADING.has(lang)) {
      this.LOADING.add(lang);
      this._http.get<Translations>(`${LEARNING_HUB_I18N_URL}${lang}.json`).subscribe({
        next: (translations: Translations) => {
          this.CACHE.set(lang, translations || {});
          this.CHANGES$.next();
        },
        // Keep the app language for this session instead of retrying on every change detection.
        error: () => this.CACHE.set(lang, {})
      });
    }
    return null;
  }
}
